<script lang='ts'>
import { defineComponent, computed } from '@vue/composition-api';
import { useNotificationMdParser } from '@/_core/utils/markdown';

interface IProps {
    text: string;
    shouldClamp: boolean;
    numberOfLines: number;
}

const TextView = defineComponent<IProps>({
    name: 'TextView',
    components: {},
    props: {
        text: String,
        shouldClamp: {
            default: true,
            type: Boolean,
        },
        numberOfLines: Number,
        fillHeight: {
            default: true,
            type: Boolean,
        },
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props, ctx) {
        const { mdViewer } = useNotificationMdParser();

        const displayText = computed(() => (props.text ? mdViewer.toHtml(props.text) : ''));

        return {
            displayText,
        };
    },
});

export default TextView;
</script>

<template>
  <div
    class="text-viewer-container"
    :class="{'line-clamp' : shouldClamp, 'fill-height': fillHeight}"
    style="word-break: break-word"
    :style="{'-webkit-line-clamp': numberOfLines}"
    v-html="displayText"
  />
</template>

<style lang='scss' scoped>
.text-viewer-container ::v-deep {
    p,
    ul,
    ol {
        margin-bottom: 8px;
    }

    pre {
        overflow-x: auto;
        overflow-y: hidden;
    }

    img {
        max-width: 100%;
    }
    .theme--light.v-application & {
        hr {
            background-color: var(--v-secondaryText-base) !important;
        }
    }
    h1 {
        font-weight: 500;
        font-size: 16px;
    }
    h2 {
        font-weight: 500;
        font-size: 14px;
    }
    h3,
    h4,
    h5,
    h6 {
        font-weight: 500;
        font-size: 12px;
    }

    table {
        border-collapse: collapse;

        td,
        th {
            vertical-align: top;
            padding: 4px 8px;
            border: 1px solid var(--v-blueGrey90Light-base);
        }
    }

    pre {
        background-color: var(--v-greyText-lighten4);
        color: var(--v-greyText-darken1);
        padding: 0.4rem;

        > code {
            background-color: transparent;
            color: inherit;
            padding: 0;
        }
    }

    @keyframes highlightIns {
        from {
            background-color: transparent;
        }
        to {
            background-color: var(--v-green10Light-base);
        }
    }

    ins {
        text-decoration: none;
        animation: highlightIns 1.2s ease-in-out forwards;
    }

    // Diff highlighting for deleted text (used in description diff dialog and AI dialogs)
    del {
        background-color: var(--v-red10Light-base, rgba(244, 67, 54, 0.15));
        color: var(--v-red100Light-base, #d32f2f);
        text-decoration: line-through;
        padding: 1px 2px;
        border-radius: 2px;
    }
}

.text-viewer-container.line-clamp ::v-deep p {
    margin-bottom: 0;
}
</style>